/* eslint-disable react-refresh/only-export-components */
import IMAGES from '@/assets/images';
import CustomImage from '@/components/CustomImage';
import { EyeIcon } from '@/icons';
import { formatDate } from '@/utils';
import { Button, Space, type TableProps } from 'antd';
import { useSearchParams } from 'react-router';
import { useGetAllLeaveApplicationsQuery } from '../../../../../../app/features/employee/emp-leave/emp-leave.api';
import type {
  GetLeaveApplicationsParams,
  LeaveApplication,
} from '../../../../../../app/features/employee/emp-leave/emp-leave.type';
import CustomTable from '../../../../../../components/CustomTable';
import StatusTag from '../../../../../../components/StatusTag';
import useAuth from '../../../../../../hooks/useAuth';
import useFilters from '../../../../../../hooks/useFilters';
import useModal from '../../../../../../hooks/useModal';
import LeaveDetails from './LeaveDetails';

export const LEAVE_STATUS_CONFIG = [
  { label: 'Pending', value: 'pending', className: 'status status-yellow' },
  { label: 'Approved', value: 'approved', className: 'status status-success' },
  { label: 'Rejected', value: 'rejected', className: 'status status-error' },
];

export default function LeaveRequest() {
  const { organizationId } = useAuth();
  const { open, handleOpen, handleClose } = useModal<{ id: string }, 'VIEW'>();
  const [searchParams] = useSearchParams();
  const { limit, page, handlePagination } =
    useFilters<GetLeaveApplicationsParams>();
  // const date = searchParams.get('date');
  const status = searchParams.get('status');
  const dept = searchParams.get('department');
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');

  const {
    data: applications,
    isLoading,
    isFetching,
  } = useGetAllLeaveApplicationsQuery({
    params: {
      page,
      pageSize: limit,
      dept: dept ? dept : undefined,
      organization_id: organizationId,
      status: status ? status.toUpperCase() : undefined,
      startDate: startDate ? startDate : undefined,
      endDate: endDate ? endDate : undefined,
    },
  });

  const columns: TableProps<any>['columns'] = [
    {
      title: 'ID',
      dataIndex: 'employee',
      align: 'center',
      render: (val) => val?.employee_id ?? 'n/a',
    },
    {
      title: 'Image',
      dataIndex: 'employee',
      render: (record) => (
        <CustomImage
          width={32}
          height={32}
          src={record?.photo || IMAGES.avatar}
          className="mx-auto rounded-full object-cover"
        />
      ),
    },
    {
      title: 'Employee Name',
      dataIndex: 'employee',
      render: (employee) => (
        <div className="flex items-center gap-3">
          <span className="font-medium">{employee?.name || 'n/a'}</span>
        </div>
      ),
    },
    {
      title: 'Department',
      dataIndex: 'employee',
      align: 'center',
      render: (employee) => (
        <div className="font-medium">{employee?.department || 'n/a'}</div>
      ),
    },
    {
      title: 'Leave Type',
      dataIndex: 'leave_type',
      align: 'center',
      render: (val) => val ?? 'n/a',
    },
    {
      title: 'Applied Date',
      dataIndex: 'applied_at',
      render: (val) => (val ? new Date(val).toLocaleDateString() : 'n/a'),
    },
    // {
    //   title: "Leave Dates",
    //   align: "center",
    //   dataIndex: ["start_date", "end_date"],
    //   render: (_, record) => {
    //     const { start_date, end_date } = record || {};
    //     const startDate = start_date
    //       ? formatDate(start_date, "MMM DD, YY")
    //       : null;
    //     const endDate = end_date ? formatDate(end_date, "MMM DD, YY") : null;

    //     return {
    //       {
    //       record?.num_days === 1 ?  startDate && endDate ? `${startDate}` : "n/a" :  startDate && endDate ? `${startDate} - ${endDate}` : "n/a"
    //       }
    //   };
    //   },
    // },
    {
      title: 'Leave Dates',
      align: 'center',
      render: (_, record) => {
        const { start_date, end_date, num_days } = record || {};

        if (!start_date || !end_date) return 'N/A';

        const startDate = formatDate(start_date, 'MMM DD, YY');
        const endDate = formatDate(end_date, 'MMM DD, YY');

        // If leave is only 1 day, show single date
        if (Number(num_days) === 1) {
          return startDate;
        }

        // Otherwise show date range
        return `${startDate} - ${endDate}`;
      },
    },
    {
      title: 'Days',
      dataIndex: 'num_days',
      align: 'center',
      render: (val) => val ?? 'n/a',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      render: (status: string) => (
        <StatusTag value={status} config={LEAVE_STATUS_CONFIG} />
      ),
    },
    {
      title: 'Actions',
      align: 'center',
      render: (record) => (
        <Space>
          <Button
            size="small"
            className="action-btn"
            icon={<EyeIcon size={20} className="text-[22px]" />}
            onClick={() => handleOpen({ type: 'VIEW', data: record })}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <CustomTable
        limit={limit}
        page={page}
        hideRowSelect
        columns={columns}
        onPaginate={handlePagination}
        tableRootClassName="!border-0 !p-0"
        customTitle="Request & Approval List"
        loading={isLoading || isFetching}
        dataSource={applications?.data?.data || []}
        total={applications?.data?.meta?.total || 0}
      />

      {/* Leave Details Drawer */}
      <LeaveDetails
        data={open.data as LeaveApplication}
        type={open.type}
        handleClose={handleClose}
        open={open.open && open.type === 'VIEW'}
      />
    </>
  );
}
